import { getDownloadURL, ref } from 'firebase/storage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { storage } from '../../lib/firebase';
import type { CardPrintPost } from './useCardPrintPosts';

/**
 * Storage path → download URL の解決 hook (Sprint BG-DataSettings card-print 完成版)。
 *
 * 旧コードは Storage download URL 解決を「別 sprint」と punt していたが、
 * 本 sprint で `getDownloadURL` を被せて PDF 生成 / preview 両方で実画像を扱えるようにする。
 *
 * 仕様:
 *   - `photoThumbPath` を優先 (印刷品質より速度優先、A4 名刺サイズ 40x55mm では 320px 程度で十分)
 *   - http(s):// で始まる場合は既に絶対 URL なので解決不要、そのまま返す
 *   - 解決失敗 (権限 / 削除) は null として記録、 silent fall back (枠だけ "(写真なし)")
 *   - postId 単位の Map を返却、React state なので `<img src>` 用に直接渡せる
 *   - 同じ post を 2 回 fetch しない (key === postId)
 */
export function useResolvedPhotoUrls(
  posts: ReadonlyArray<CardPrintPost>,
): Record<string, string | null> {
  const [resolved, setResolved] = useState<Record<string, string | null>>({});
  const inflightRef = useRef<Set<string>>(new Set());

  const targets = useMemo(() => {
    const out: Array<{ postId: string; path: string; absolute: boolean }> = [];
    for (const p of posts) {
      // docs/28: 投稿 doc の URL を優先する (ぼかしの作り直しで URL が変わったら追従する)。
      const raw = p.photoThumbUrl ?? p.photoUrl ?? p.photoThumbPath ?? p.photoPath;
      if (!raw) continue;
      if (/^https?:\/\//.test(raw)) {
        out.push({ postId: p.id, path: raw, absolute: true });
      } else {
        out.push({ postId: p.id, path: raw, absolute: false });
      }
    }
    return out;
  }, [posts]);

  useEffect(() => {
    let cancelled = false;
    const toFetch = targets.filter(
      (t) => !t.absolute && resolved[t.postId] === undefined && !inflightRef.current.has(t.postId),
    );

    // 既に絶対 URL のものは即座に state へ反映
    const immediate: Record<string, string | null> = {};
    for (const t of targets) {
      if (t.absolute && resolved[t.postId] !== t.path) {
        immediate[t.postId] = t.path;
      }
    }
    if (Object.keys(immediate).length > 0) {
      setResolved((prev) => ({ ...prev, ...immediate }));
    }

    if (toFetch.length === 0) return;

    for (const t of toFetch) {
      inflightRef.current.add(t.postId);
    }

    void Promise.all(
      toFetch.map(async (t) => {
        try {
          const url = await getDownloadURL(ref(storage, t.path));
          return [t.postId, url] as const;
        } catch {
          return [t.postId, null] as const;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setResolved((prev) => {
        const next = { ...prev };
        for (const [postId, url] of results) {
          next[postId] = url;
          inflightRef.current.delete(postId);
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [targets, resolved]);

  return resolved;
}
